import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { Logo } from "./Logo";

export const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 80, behavior: "smooth" });
};

export const Nav = () => {
  const { lang, setLang, t } = useLang();
  const [open, setOpen] = useState(false);

  const links = [
    { id: "services", label: t.nav.services },
    { id: "offers", label: t.nav.offers },
    { id: "manifesto", label: t.nav.why },
    { id: "about", label: t.nav.about },
    { id: "faq", label: "FAQ" },
    { id: "contact", label: t.nav.contact },
  ];

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      className="fixed top-0 inset-x-0 z-40 border-b border-white/10 bg-black/60 backdrop-blur-2xl"
      data-testid="main-nav"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex items-center gap-3 group"
          data-testid="nav-logo"
        >
          <span className="transition-transform duration-300 group-hover:rotate-12">
            <Logo size={36} intro />
          </span>
          <span className="font-display font-semibold tracking-tight text-base md:text-lg leading-none">
            Smart <span className="text-[#FF7900]">Telecom</span>{" "}Advisor
          </span>
          <span className="hidden md:inline-flex bg-white rounded-sm px-2 py-1" data-testid="nav-smart-sn-logo">
            <img src="/smart-sn-logo.webp" alt="Smart TA" className="w-16" />
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-10">
          {links.map((l) => (
            <button
              key={l.id}
              onClick={() => scrollToId(l.id)}
              className="text-sm text-white/70 hover:text-white transition-colors duration-300 tracking-wide"
              data-testid={`nav-link-${l.id}`}
            >
              {l.label}
            </button>
          ))}
          <Link
            to="/blog"
            className="text-sm text-white/70 hover:text-white transition-colors duration-300 tracking-wide"
            data-testid="nav-link-blog"
          >
            {t.nav.blog}
          </Link>
        </nav>

        <div className="flex items-center gap-3 md:gap-5">
          <div className="flex items-center border border-white/15 rounded-full p-1" data-testid="language-switcher">
            {["fr", "en"].map((l) => (
              <button
                key={l}
                onClick={() => setLang(l)}
                data-testid={`lang-${l}-button`}
                className={`px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-widest transition-colors duration-300 ${
                  lang === l ? "bg-[#FF7900] text-white" : "text-white/50 hover:text-white"
                }`}
              >
                {l}
              </button>
            ))}
          </div>
          <button
            onClick={() => scrollToId("contact")}
            data-testid="nav-cta-button"
            className="hidden md:inline-flex items-center bg-[#FF7900] hover:bg-white hover:text-black text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-colors duration-300"
          >
            {t.nav.cta}
          </button>
          <button
            className="lg:hidden text-white/80"
            onClick={() => setOpen(!open)}
            data-testid="nav-menu-toggle"
            aria-label="Menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden overflow-hidden border-t border-white/10 bg-black/90 backdrop-blur-2xl"
            data-testid="nav-mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {links.map((l) => (
                <button
                  key={l.id}
                  onClick={() => {
                    setOpen(false);
                    scrollToId(l.id);
                  }}
                  className="text-left text-lg text-white/80 hover:text-[#FF7900] transition-colors duration-300"
                  data-testid={`nav-mobile-link-${l.id}`}
                >
                  {l.label}
                </button>
              ))}
              <Link
                to="/blog"
                onClick={() => setOpen(false)}
                className="text-left text-lg text-white/80 hover:text-[#FF7900] transition-colors duration-300"
                data-testid="nav-mobile-link-blog"
              >
                {t.nav.blog}
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
